import { useState } from "react";
import ProviderDetailTabs from "./ProviderDetailTabs";
import StageSummaryCard from "./StageSummaryCard";
import StageCrdForm from "./StageCrdForm";
import StageDtrForm from "./StageDtrForm";
import StagePasForm from "./StagePasForm";
import CptCodesPanel from "./CptCodesPanel";
import {
  DEFAULT_UNMATCHED_CPT_POLICY,
  ServiceProvider,
  StageKey,
  UnmatchedCptPolicyId,
} from "../../../utils/integrationConfigData";
import { AuthTypeApi } from "../types/providerIntegrationtypes";

interface ProviderDetailProps {
  provider: ServiceProvider | null;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  cptImportNonce?: number;
  showCptTab?: boolean;
  defaultProviderLabel?: string;
  unmatchedPolicy?: UnmatchedCptPolicyId;
  onUnmatchedPolicyChange?: (value: UnmatchedCptPolicyId) => void;
  authTypes?: AuthTypeApi[];
  onProvidersChanged?: () => void;
}

const STAGE_KEYS: StageKey[] = ["crd", "dtr", "pas"];

const getInitials = (provider: ServiceProvider) => {
  const source = provider.shortName || provider.name;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const ProviderDetail = ({
  provider,
  activeTab,
  onTabChange,
  cptImportNonce = 0,
  showCptTab = true,
  defaultProviderLabel,
  unmatchedPolicy,
  onUnmatchedPolicyChange,
  authTypes = [],
  onProvidersChanged,
}: ProviderDetailProps) => {
  const [internalTab, setInternalTab] = useState("summary");
  const [internalUnmatchedPolicy, setInternalUnmatchedPolicy] =
    useState<UnmatchedCptPolicyId>(DEFAULT_UNMATCHED_CPT_POLICY);
  const isControlled = activeTab !== undefined;
  const activeDetailTab = isControlled ? activeTab : internalTab;
  const unmatchedValue = unmatchedPolicy ?? internalUnmatchedPolicy;

  const setActiveDetailTab = (tabId: string) => {
    if (onTabChange) {
      onTabChange(tabId);
    }
    if (!isControlled) {
      setInternalTab(tabId);
    }
  };

  const handleUnmatchedPolicyChange = (value: UnmatchedCptPolicyId) => {
    if (onUnmatchedPolicyChange) {
      onUnmatchedPolicyChange(value);
      return;
    }
    setInternalUnmatchedPolicy(value);
  };

  if (!provider) {
    return (
      <div className="ic-provider-detail ic-provider-detail-empty">
        <p className="mb-0">Select a service provider to view configuration.</p>
      </div>
    );
  }

  const stages = provider.stages;
  const routeDefaultLabel =
    defaultProviderLabel || provider.shortName || provider.name;
  const pathParts = provider.path
    .split("→")
    .map((part) => part.trim())
    .filter(Boolean);
  return (
    <section className="ic-provider-detail" aria-label={provider.name}>
      <header className="ic-provider-detail-header">
        <div className="ic-provider-detail-identity">
          <span className="ic-provider-avatar" aria-hidden="true">
            {getInitials(provider)}
          </span>

          <div className="ic-provider-detail-copy">
            <div className="ic-provider-detail-title-row">
              <h2 className="ic-provider-detail-title">{provider.name}</h2>
            </div>

            <nav
              className="ic-provider-breadcrumb"
              aria-label="Provider location"
            >
              {pathParts.map((part, index) => (
                <span key={`${part}-${index}`} className="ic-provider-crumb">
                  {index > 0 && (
                    <span className="ic-provider-crumb-sep" aria-hidden="true">
                      →
                    </span>
                  )}
                  <span
                    className={
                      index === pathParts.length - 1
                        ? "ic-provider-crumb-current"
                        : undefined
                    }
                  >
                    {part}
                  </span>
                </span>
              ))}
            </nav>
          </div>
        </div>

        <div className="ic-provider-meta-strip">
          <div className="ic-provider-status-chip">
            <span className="ic-provider-status-dot" aria-hidden="true" />
            <span className="ic-provider-meta-value">{provider.status}</span>
          </div>
        </div>
      </header>

      <ProviderDetailTabs
        activeTab={activeDetailTab}
        onChange={setActiveDetailTab}
        provider={provider}
        showCptTab={showCptTab}
      />

      {activeDetailTab === "summary" && (
        <div className="ic-summary-content">
          <p className="ic-summary-intro">
            Review each integration stage for{" "}
            {provider.shortName || provider.name}. Use the tabs above to
            configure endpoints and authentication.
          </p>
          <div className="ic-stage-tile-grid">
            {STAGE_KEYS.map((stageKey) => (
              <StageSummaryCard
                key={stageKey}
                stageKey={stageKey}
                stage={stages[stageKey]}
                onClick={() => setActiveDetailTab(stageKey)}
              />
            ))}
          </div>
          {showCptTab && (
            <p className="ic-detail-hint">
              CPT ownership for this vendor is managed on the CPT Codes tab.
            </p>
          )}
        </div>
      )}

      {activeDetailTab === "crd" && (
        <div className="ic-provider-stage-form">
          <StageCrdForm
            provider={provider}
            stage={stages.crd}
            showInfoBanner={false}
            authTypes={authTypes}
            onProvidersChanged={onProvidersChanged}
          />
        </div>
      )}

      {activeDetailTab === "dtr" && (
        <div className="ic-provider-stage-form">
          <StageDtrForm
            provider={provider}
            stage={stages.dtr}
            showInfoBanner={false}
            authTypes={authTypes}
            onProvidersChanged={onProvidersChanged}
          />
        </div>
      )}

      {activeDetailTab === "pas" && (
        <div className="ic-provider-stage-form">
          <StagePasForm
            provider={provider}
            stage={stages.pas}
            showInfoBanner={false}
            authTypes={authTypes}
            onProvidersChanged={onProvidersChanged}
          />
        </div>
      )}

      {showCptTab && activeDetailTab === "cpt" && (
        <div className="ic-provider-stage-form">
          <CptCodesPanel
            provider={provider}
            importNonce={cptImportNonce}
            compact
            defaultProviderLabel={routeDefaultLabel}
            unmatchedPolicy={unmatchedValue}
            onUnmatchedPolicyChange={handleUnmatchedPolicyChange}
            onProvidersChanged={onProvidersChanged}
          />
        </div>
      )}
    </section>
  );
};

export default ProviderDetail;
